import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import { useAuth } from '../../lib/auth';
import AppHeader from '../AppHeader';
import { errorBox, inputClass } from '../AuthLayout';
import People from './People';
import Brand from './Brand';
import Activity from './Activity';
import { hasPaidAccess } from '../../utils/billing';
import { UpgradeNote } from '../Upgrade';

const TABS = [
  { id: 'people', label: 'People' },
  { id: 'brand', label: 'Brand' },
  { id: 'activity', label: 'Activity' },
];

/** Companies the signed-in person administers (owner/admin), for the nav link and switcher. */
export function useAdminCompanies() {
  const { memberships, canManageOrg } = useAuth();
  return memberships
    .filter((m) => m.organizations?.kind === 'company' && canManageOrg(m.org_id))
    .map((m) => m.organizations);
}

/** /company/:orgId?tab=people|brand|activity — company owners and admins manage their company. */
export default function CompanyAdmin() {
  const { orgId } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { memberships, canManageOrg, isPlatformAdmin, user } = useAuth();
  const companies = useAdminCompanies();
  const allowed = canManageOrg(orgId);
  const tab = TABS.find((t) => t.id === params.get('tab'))?.id || 'people';

  const [org, setOrg] = useState(null);
  const [error, setError] = useState('');

  const loadOrg = useCallback(async () => {
    const { data, error: err } = await supabase.from('organizations')
      .select('id, name, kind, status, seat_limit, plan, billing_status, comp_until, offer, stripe_customer_id').eq('id', orgId).maybeSingle();
    if (err) setError(err.message);
    else if (!data) setError('Company not found.');
    else setOrg(data);
  }, [orgId]);

  useEffect(() => {
    setOrg(null);
    setError('');
    if (allowed) loadOrg();
  }, [allowed, loadOrg]);

  if (!allowed) {
    return (
      <Shell>
        <div className="bg-white rounded-2xl border border-line p-8 sm:p-10 text-center max-w-xl mx-auto mt-6">
          <div className="w-12 h-12 mx-auto rounded-full bg-coral-soft flex items-center justify-center mb-4" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#B9302D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
          </div>
          <h1 className="text-2xl font-bold">You can't manage this company</h1>
          <p className="text-ink-muted mt-2">
            Only a company's owners and admins can open its company settings. If you think you should have access, ask your company admin.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-5">
            <Link to="/" className="text-sm font-semibold text-blue hover:underline">Back to my cards</Link>
            {companies[0] && <Link to={`/company/${companies[0].id}`} className="text-sm font-semibold text-blue hover:underline">Open {companies[0].name}</Link>}
          </div>
        </div>
      </Shell>
    );
  }

  if (error) return <Shell><div className={errorBox}>{error}</div></Shell>;
  if (!org) return <Shell><p className="text-ink-muted">Loading company...</p></Shell>;
  if (org.kind !== 'company') {
    return (
      <Shell>
        <div className="bg-white rounded-2xl border border-line p-8 text-center max-w-xl mx-auto">
          <h1 className="text-2xl font-bold">This is a personal account</h1>
          <p className="text-ink-muted mt-2">Personal accounts have one card. Change its colors and logo in the card editor.</p>
          <Link to="/" className="inline-block mt-4 text-sm font-semibold text-blue hover:underline">Back to my cards</Link>
        </div>
      </Shell>
    );
  }

  const myRole = memberships.find((m) => m.org_id === orgId && m.status === 'active')?.role || null;
  const setTab = (id) => setParams(id === 'people' ? {} : { tab: id }, { replace: true });
  // Teams features: Carduzo platform admins can always set a company up (e.g. a free company's brand)
  const teams = isPlatformAdmin || hasPaidAccess(org);
  const switcher = companies.length > 1 || (isPlatformAdmin && !companies.some((c) => c.id === orgId));

  return (
    <Shell>
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[240px]">
          <p className="text-xs font-semibold tracking-wider uppercase text-ink-muted">Company</p>
          <h1 className="text-[34px] leading-10 font-extrabold tracking-tight mt-0.5">{org.name}</h1>
          <p className="text-[15px] text-ink-muted mt-1">
            {myRole ? `Your role: ${myRole}` : 'You are viewing this company as a Carduzo platform admin.'}
            {org.status !== 'active' && <span className="text-coral-ink font-semibold"> · This company is suspended</span>}
          </p>
        </div>
        {switcher && (
          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-ink-muted">Switch company</span>
            <select
              value={orgId}
              onChange={(e) => navigate(`/company/${e.target.value}${tab === 'people' ? '' : `?tab=${tab}`}`)}
              className={`${inputClass.replace('w-full ', '')} w-auto min-w-[200px]`}
              aria-label="Switch company"
            >
              {!companies.some((c) => c.id === orgId) && <option value={orgId}>{org.name}</option>}
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
        )}
      </div>

      <div role="tablist" aria-label="Company sections" className="flex gap-1 border-b border-line overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.id} role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
            className={`h-11 px-4 -mb-px text-[15px] whitespace-nowrap border-b-2 transition ${tab === t.id ? 'border-blue text-blue font-semibold' : 'border-transparent text-ink-muted font-medium hover:text-ink'}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div role="tabpanel">
        {!hasPaidAccess(org) && isPlatformAdmin && (
          <p className="mb-5 text-sm text-ink-muted" data-testid="admin-free-note">
            This company is on the Free plan. As a Carduzo admin you can still set up its brand and people; its own admins see an upgrade prompt instead.
          </p>
        )}
        {tab === 'people' && <People org={org} myRole={myRole} isPlatformAdmin={isPlatformAdmin} userId={user.id} />}
        {tab === 'brand' && (teams
          ? <Brand org={org} userId={user.id} />
          : <TeamsTeaser org={org} canManage>
              Company branding is part of Teams: your logo and colors on every card, a default layout, and locks so every card stays on brand.
            </TeamsTeaser>)}
        {tab === 'activity' && (teams
          ? <Activity org={org} />
          : <TeamsTeaser org={org} canManage>
              Activity is part of Teams: views, saved contacts and leads for every card in the company.
            </TeamsTeaser>)}
      </div>
    </Shell>
  );
}

function TeamsTeaser({ org, canManage, children }) {
  return (
    <div className="bg-white rounded-2xl border border-line p-6 sm:p-8 max-w-2xl">
      <UpgradeNote org={org} canManage={canManage}>{children}</UpgradeNote>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-surface">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">{children}</main>
    </div>
  );
}
